import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from '../../environments/environment';
import { SubUrlsEnum } from '../enums/sub-urls.enum';
import { IAvailableEventsDTOIn, IMonthlyEventData } from '../interfaces/event.interface';
import {
    IActivateHomeworkSchoolDTOOut,
    IBookedHomeworkSchoolToCancel,
    IBookedHomeworksSchoolDTOIn,
    IBookHomeworksSchoolDTOOut,
    IHomeworkSchoolSetting,
    IHomeworkSchoolSettingsDTOOut,
    ITotalOfBookedHomeworksPerMonth,
    ITotalOfBookedHomeworksPerMonthDTOIn,
} from '../interfaces/homework-school.interface';
import { formatDateYYYYMMDD } from '../utils/date.utils';
import { PaymentStatusEnum } from './../enums/payment-status.enum';
import { MappersService } from './mappers.service';

@Injectable({
    providedIn: 'root',
})
export class HomeworkSchoolService {
    constructor(private http: HttpClient, private mapperService: MappersService) {}

    // HOMEWORK SCHOOL SETTINGS
    public getSchoolHomeworkSchoolSettings(schoolId: number): Observable<IHomeworkSchoolSetting> {
        const url: string = environment.wasoApi + SubUrlsEnum.SCHOOLS + '/' + schoolId + '/';
        return this.http.get<IHomeworkSchoolSetting>(url + SubUrlsEnum.HOMEWORK_SETTINGS);
    }

    public patchSchoolHomeworkSchoolSetting(homeworkSchoolSetting: IHomeworkSchoolSettingsDTOOut): Observable<IHomeworkSchoolSetting> {
        const url: string = environment.wasoApi + SubUrlsEnum.HOMEWORK_SETTINGS + '/' + homeworkSchoolSetting.homework_setting.id;
        return this.http.put<IHomeworkSchoolSetting>(url, homeworkSchoolSetting);
    }

    // AVAILABLE HOMEWORK SCHOOL
    public getAllAvailableHomeworkSchool(schoolId: number): Observable<IMonthlyEventData[]> {
        return this.http
            .get<IAvailableEventsDTOIn>(environment.wasoApi + SubUrlsEnum.AVAILABLE_HOMEWORK.replace('{{schoolId}}', schoolId.toString()))
            .pipe(
                map((availableHomeworkSchool: IAvailableEventsDTOIn) =>
                    this.mapperService.fromAvailableEventsToEventsData(availableHomeworkSchool)
                )
            );
    }

    // ACTIVATE HOMEWORK SCHOOL
    public activateHomeworkSchool(
        homeworkSchoolToActivate: IActivateHomeworkSchoolDTOOut,
        schoolId: number
    ): Observable<IAvailableEventsDTOIn> {
        return this.http.post<IAvailableEventsDTOIn>(
            environment.wasoApi + SubUrlsEnum.ACTIVATE_HOMEWORK.replace('{{schoolId}}', schoolId.toString()),
            homeworkSchoolToActivate
        );
    }

    // DISABLE HOMEWORK SCHOOL
    public disableHomeworkSchool(homeworkSchoolToDisable: IActivateHomeworkSchoolDTOOut, schoolId: number): Observable<void> {
        const options: { headers: HttpHeaders; body: any } = {
            headers: new HttpHeaders(),
            body: homeworkSchoolToDisable,
        };
        return this.http.delete<void>(
            environment.wasoApi + SubUrlsEnum.DISABLE_HOMEWORK.replace('{{schoolId}}', schoolId.toString()),
            options
        );
    }

    // DISABLE FULL MONTH
    public disableFullMonthHomeworkSchool(monthDate: Date, schoolId: number): Observable<void> {
        const params: HttpParams = new HttpParams().set('date', formatDateYYYYMMDD(monthDate));
        return this.http.delete<void>(
            environment.wasoApi + SubUrlsEnum.DISABLE_MONTH_HOMEWORK.replace('{{schoolId}}', schoolId.toString()),
            {
                params,
            }
        );
    }

    // BOOK HOMEWORK SCHOOL
    public bookHomeworkSchool(homeworkSchoolToBook: IBookHomeworksSchoolDTOOut): Observable<IBookedHomeworksSchoolDTOIn> {
        return this.http.post<IBookedHomeworksSchoolDTOIn>(environment.wasoApi + SubUrlsEnum.BOOK_HOMEWORK, homeworkSchoolToBook);
    }

    // BOOKED HOMEWORK SCHOOL
    public getBookedHomeworkSchool(payload: {
        from?: string;
        to?: string;
        search?: string;
        mealChoices?: string[];
        schoolId?: number;
        educationLevelId?: number;
        selectedStudentId?: number;
        homeworkTypes?: number;
        paymentStatus?: PaymentStatusEnum[];
    }): Observable<IBookedHomeworksSchoolDTOIn> {
        let params: HttpParams = new HttpParams();
        if (payload.from && payload.to) {
            params = params.set('from', payload.from);
            params = params.set('to', payload.to);
        }
        if (payload.educationLevelId) {
            params = params.set('education_level_id', payload.educationLevelId);
        }
        if (payload.search) {
            params = params.set('search', payload.search);
        }
        if (payload.selectedStudentId) {
            params = params.set('viewing_student_id', payload.selectedStudentId);
        }
        if (payload.homeworkTypes === 0 || payload.homeworkTypes === 1) {
            params = params.set('homework_type', payload.homeworkTypes);
        }
        // si y a les deux status (payé et non payé), pas besoin de préciser
        if (payload.paymentStatus?.length === 1) {
            params = params.set('paid', payload.paymentStatus[0] === PaymentStatusEnum.PAID ? 1 : 0);
        }
        return this.http.get<IBookedHomeworksSchoolDTOIn>(environment.wasoApi + SubUrlsEnum.BOOK_HOMEWORK, {
            params,
        });
    }

    public getTotalOfBookedHomeworksPerMonth(selectedStudentId?: number): Observable<ITotalOfBookedHomeworksPerMonth[]> {
        let params: HttpParams = new HttpParams();
        if (selectedStudentId) {
            params = params.set('viewing_student_id', selectedStudentId);
        }
        return this.http
            .get<ITotalOfBookedHomeworksPerMonthDTOIn>(environment.wasoApi + SubUrlsEnum.BOOK_HOMEWORK_BY_MONTH, {
                params: selectedStudentId ? params : null,
            })
            .pipe(map((result: ITotalOfBookedHomeworksPerMonthDTOIn) => result.book_homeworks));
    }

    public cancelHomeworkSchool(idsToCancelToCancel: IBookedHomeworkSchoolToCancel): Observable<void> {
        const options: { headers: HttpHeaders; body: any } = {
            headers: new HttpHeaders(),
            body: idsToCancelToCancel,
        };
        return this.http.delete<void>(environment.wasoApi + SubUrlsEnum.CANCEL_BOOK_HOMEWORK, options);
    }
}
